/**
 * Mordeczka.eu Admin - Generic Dictionary Page with Ant Design
 */

import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { PaginatedResponse, PaginationParams } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

interface DictionaryItem {
  id: string;
  name: string;
  display_order?: number;
  created_at?: string;
}

interface DictionaryPageProps<T extends DictionaryItem> {
  title: string;
  queryKey: string;
  api: {
    list: (params?: PaginationParams) => Promise<PaginatedResponse<T>>;
    create: (data: any) => Promise<T>;
    update: (id: string, data: any) => Promise<T>;
    delete: (id: string) => Promise<void>;
  };
  hasDisplayOrder?: boolean;
}

export function DictionaryPage<T extends DictionaryItem>({
  title,
  queryKey,
  api,
  hasDisplayOrder = false,
}: DictionaryPageProps<T>) {
  const { canEdit, canDelete } = usePermissions();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText, setDebouncedSearchText] = useState("");

  // Debounce search text
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
      if (searchText !== debouncedSearchText) {
        setPagination((prev) => ({ ...prev, current: 1 }));
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const { data, isLoading } = useQuery({
    queryKey: [queryKey, pagination, debouncedSearchText],
    queryFn: () =>
      api.list({
        page: pagination.current,
        page_size: pagination.pageSize,
        search: debouncedSearchText,
      }),
  });

  const createMutation = useMutation({
    mutationFn: api.create,
    onSuccess: () => {
      message.success(`${title.slice(0, -1)} utworzono pomyślnie`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.update(id, data),
    onSuccess: () => {
      message.success(`${title.slice(0, -1)} zaktualizowano pomyślnie`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const deleteMutation = useMutation({
    mutationFn: api.delete,
    onSuccess: () => {
      message.success(`${title.slice(0, -1)} usunięto pomyślnie`);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const openModal = (item?: T) => {
    setEditingItem(item || null);
    const initialValues: any = { name: item?.name || "" };
    if (hasDisplayOrder) {
      initialValues.display_order =
        item?.display_order !== undefined ? item.display_order : 100;
    }
    form.setFieldsValue(initialValues);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch (error) {
      // Validation failed
    }
  };

  const columns: ColumnsType<T> = [
    ...(hasDisplayOrder
      ? [
          {
            title: "Kolejność",
            dataIndex: "display_order",
            key: "display_order",
            width: 100,
            sorter: (a: T, b: T) =>
              (a.display_order || 0) - (b.display_order || 0),
            defaultSortOrder: "ascend" as const,
          },
        ]
      : []),
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: T) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć ten element?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            okButtonProps={{ danger: true }}
            cancelText="Anuluj"
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          {title}
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nowy
        </Button>
      </div>

      <Card>
        <Input.Search
          placeholder="Szukaj..."
          onChange={(e) => setSearchText(e.target.value)}
          style={{ marginBottom: 16, maxWidth: 300 }}
          allowClear
        />
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "500", "1000"],
            showTotal: (total) => `Łącznie ${total} elementów`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={
          editingItem
            ? `Edytuj ${title.slice(0, -1)}`
            : `Dodaj ${title.slice(0, -1)}`
        }
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nazwa"
            rules={[{ required: true, message: "Proszę wprowadzić nazwę" }]}
          >
            <Input placeholder="Wprowadź nazwę" />
          </Form.Item>

          {hasDisplayOrder && (
            <Form.Item
              name="display_order"
              label="Kolejność"
              rules={[
                { required: true, message: "Proszę wprowadzić kolejność" },
              ]}
              initialValue={100}
            >
              <InputNumber style={{ width: "100%" }} />
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
}

export default DictionaryPage;
